// @unocss-include
'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useDrag } from '@use-gesture/react';
import classNames from 'clsx';
import { createContext, useCallback, useContext, useRef, useState, } from 'react';
import useMergedRef from '../../hooks/useMergedRef.js';
import { withClassName } from '../../hooks/withClassName.js';
import { BoxContext } from '../box/Box.js';
import { Button } from '../button/index.js';
import { useParticles } from '../particles/index.js';
import { useConfig } from '../provider/Provider.js';
import { selectTriggerClassName } from '../select/index.js';
const StyledOverlay = withClassName(DialogPrimitive.Overlay, 'layer-components:fixed layer-components:inset-0 layer-components:z-dialog-backdrop layer-components:animate-fade-in layer-components:animate-duration-200 layer-components:bg-overlay layer-components:[box-shadow:inset_0_30px_60px_0px_var(--color-overlay)] layer-components:border-top-1 layer-components:border-top-solid layer-components:border-top-gray', 'layer-components:[&[data-state=closed]]:animate-fade-out', 'motion-reduce:animate-none');
const StyledContent = withClassName(DialogPrimitive.Content, 'layer-components:z-dialog layer-components:fixed layer-components:shadow-xl-up layer-components:bg-white layer-components:overflow-y-auto layer-components:flex layer-components:flex-col layer-components:border-top-1 layer-components:border-top-solid layer-components:border-top-gray', 'layer-components:sm:shadow-xl', 'transform-gpu !motion-reduce:animate-none', 'layer-components:left-50% layer-components:top-50% layer-components:translate-[-50%] layer-components:w-90vw layer-components:max-w-450px layer-components:max-h-85vh layer-components:p-6 layer-components:pt-8 layer-components:rounded-lg layer-components:border-b-1 layer-components:pt-6', 'layer-components:animate-dialog-in layer-components:[&[data-state=closed]]:animate-dialog-out layer-components:motion-reduce:animate-none');
const sheetClassNames = classNames('layer-variants:lt-sm:translate-0 layer-variants:lt-sm:left-0 layer-variants:lt-sm:right-0 layer-variants:lt-sm:top-auto layer-variants:lt-sm:h-min-content layer-variants:lt-sm:rounded-tl-xl layer-variants:lt-sm:rounded-tr-xl layer-variants:lt-sm:rounded-b-0 layer-variants:lt-sm:p-6 layer-variants:lt-sm:pt-8 layer-variants:lt-sm:w-full layer-variants:lt-sm:max-w-none layer-variants:lt-sm:pb-[calc(3rem+env(safe-area-inset-bottom,0px))] layer-variants:lt-sm:border-b-0', 'layer-variants:lt-sm:animate-ease-in layer-variants:lt-sm:animate-fade-in-up layer-variants:lt-sm:[&[data-state=closed]]:animate-fade-out-down');
const sheetClassNameWithOverlayKeyboard = classNames('layer-variants:lt-sm:bottom-[calc(var(--mock-virtual-keyboard-height,env(keyboard-inset-height,0px))+var(--gesture-y,0px))] layer-variants:lt-sm:max-h-[calc(95vh-var(--mock-virtual-keyboard-height,env(keyboard-inset-height,0px)))]');
const sheetClassNameWithDisplaceKeyboard = classNames('layer-variants:lt-sm:bottom-[calc(var(--viewport-bottom-offset,0px)+var(--gesture-y,0px))] layer-variants:lt-sm:max-h-[calc(0.85*var(--viewport-height,100vh))]');
export const Content = function Content(_a) {
    var { ref, children, width, outerClassName, className, disableSheet } = _a, props = __rest(_a, ["ref", "children", "width", "outerClassName", "className", "disableSheet"]);
    const particles = useParticles();
    const wasOpenRef = useRef(false);
    const openRef = useCallback((element) => {
        if (!wasOpenRef.current &&
            (element === null || element === void 0 ? void 0 : element.getAttribute('data-state')) === 'open') {
            wasOpenRef.current = true;
            const matchesSmall = !disableSheet &&
                typeof window !== 'undefined' &&
                !window.matchMedia('(min-width:600px)').matches;
            if (!matchesSmall)
                return;
            setTimeout(() => {
                particles === null || particles === void 0 ? void 0 : particles.addParticles(particles.elementExplosion({
                    count: 20,
                    margin: 40,
                    borders: ['top'],
                    color: [
                        {
                            space: 'rgb',
                            values: [0, 0, 0],
                            opacity: 0.02,
                        },
                        {
                            space: 'rgb',
                            values: [0, 0, 0],
                            opacity: 0,
                        },
                    ],
                    element,
                    startRadius: 15,
                    endRadius: 0,
                    lifespan: 1000,
                    force: 0.5,
                    drag: 0.01,
                    forceFuzz: 0.5,
                    angleFuzz: 0.1,
                }));
            }, 180);
        }
        else if ((element === null || element === void 0 ? void 0 : element.getAttribute('data-state')) === 'closed') {
            wasOpenRef.current = false;
        }
    }, [particles, disableSheet]);
    const gestureRef = useRef(null);
    const finalRef = useMergedRef(ref, openRef, gestureRef);
    const { virtualKeyboardBehavior } = useConfig();
    return (_jsxs(DialogPrimitive.Portal, { children: [_jsx(StyledOverlay, {}), _jsx(BoxContext.Provider, { value: { spacingScale: 1 }, children: _jsxs(StyledContent, Object.assign({ "data-dialog-content": true, ref: finalRef }, props, { className: classNames({
                        'md:max-w-800px': width === 'lg',
                        'max-w-600px': width === 'md',
                        'max-w-300px': width === 'sm',
                    }, !disableSheet && sheetClassNames, !disableSheet &&
                        virtualKeyboardBehavior === 'overlay' &&
                        sheetClassNameWithOverlayKeyboard, !disableSheet &&
                        virtualKeyboardBehavior === 'displace' &&
                        sheetClassNameWithDisplaceKeyboard, outerClassName || className), children: [!disableSheet && _jsx(DialogSwipeHandle, {}), children] })) })] }));
};
export const DialogSwipeHandle = function DialogSwipeHandle(_a) {
    var { ref, className } = _a, props = __rest(_a, ["ref", "className"]);
    const close = useContext(DialogCloseContext);
    const innerRef = useRef(null);
    useDrag(({ swipe: [, swipeY], movement: [, dy], velocity: [, vy], last }) => {
        const content = findParentDialogContent(innerRef.current);
        if (!content)
            return;
        const contentHeight = content.clientHeight;
        const shouldClose = last && (swipeY === 1 || dy > contentHeight / 2);
        if (shouldClose) {
            close();
        }
        const gestureY = last ? (shouldClose ? -1000 : 0) : -Math.max(0, dy);
        console.log(gestureY, dy, vy, swipeY);
        content.style.setProperty('--gesture-y', `${gestureY}px`);
        content.style.setProperty('transition', last ? 'bottom 0.2s' : '');
    }, {
        target: innerRef,
        axis: 'y',
    });
    const finalRef = useMergedRef(ref, innerRef);
    return (_jsx("div", Object.assign({ ref: finalRef }, props, { className: classNames('absolute top-0 left-50% transform-gpu -translate-x-1/2 w-20% py-2 rounded-lg cursor-grab sm:hidden touch-none', className), children: _jsx("div", { className: "w-full h-[4px] bg-gray rounded-lg" }) })));
};
function findParentDialogContent(element) {
    if (!element)
        return null;
    if (element.getAttribute('data-dialog-content'))
        return element;
    return findParentDialogContent(element.parentElement);
}
const DialogCloseContext = createContext(() => { });
const StyledTitle = withClassName(DialogPrimitive.Title, 'font-title color-black text-3xl mb-4 mt-0');
const StyledDescription = withClassName(DialogPrimitive.Description, 'mt-3 mb-6 color-gray-dark text-md leading-6');
// Exports
const DialogRoot = (props) => {
    var _a;
    const [innerOpen, innerOnOpenChange] = useState(props.defaultOpen);
    const open = (_a = props.open) !== null && _a !== void 0 ? _a : innerOpen;
    const onOpenChange = useCallback((open) => {
        var _a;
        innerOnOpenChange(open);
        (_a = props.onOpenChange) === null || _a === void 0 ? void 0 : _a.call(props, open);
    }, [props.onOpenChange]);
    const close = useCallback(() => {
        onOpenChange(false);
    }, [onOpenChange]);
    return (_jsx(DialogCloseContext.Provider, { value: close, children: _jsx(DialogPrimitive.Root, Object.assign({}, props, { open: open, onOpenChange: onOpenChange })) }));
};
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogContent = Content;
export const DialogTitle = StyledTitle;
export const DialogDescription = StyledDescription;
export const DialogClose = function DialogClose(_a) {
    var { ref, asChild, children } = _a, props = __rest(_a, ["ref", "asChild", "children"]);
    return (_jsx(DialogPrimitive.DialogClose, Object.assign({ asChild: true, ref: ref }, props, { children: asChild === true ? children : _jsx(Button, { children: children !== null && children !== void 0 ? children : 'Close' }) })));
};
export const DialogActions = withClassName('div', 'flex justify-end sticky w-full gap-3 items-center bg-white py-4 translate-y-6 flex-wrap', 'bottom--6', 'sm:bottom-0');
export const DialogSelectTrigger = function DialogSelectTrigger(_a) {
    var { ref, children, className } = _a, props = __rest(_a, ["ref", "children", "className"]);
    return (_jsxs(DialogPrimitive.Trigger, Object.assign({ className: classNames(selectTriggerClassName, className) }, props, { children: [_jsx("span", { children: children }), _jsx(ChevronDownIcon, {})] })));
};
export const DialogSelectList = withClassName(RadioGroupPrimitive.Root, 'flex flex-col gap-2 overflow-y-auto p-2');
export const DialogSelectItemRoot = withClassName(RadioGroupPrimitive.Item, 'flex items-center gap-3 w-full py-3 px-4 text-left border-none rounded-lg font-normal bg-transparent [&:nth-child(2n+1)]:bg-gray-blend cursor-pointer transition-all', '[&[data-state=checked]]:bg-primary-wash [&[data-state=checked]]:text-primary-dark');
export const DialogSelectItem = function DialogSelectItem(_a) {
    var { ref, children } = _a, props = __rest(_a, ["ref", "children"]);
    return (_jsxs(DialogSelectItemRoot, Object.assign({}, props, { children: [_jsx("span", { className: "flex-1", children: children }), _jsx(RadioGroupPrimitive.Indicator, { className: "flex-0-0-auto", children: _jsx(CheckIcon, {}) })] })));
};
export const Dialog = Object.assign(DialogRoot, {
    Trigger: DialogTrigger,
    Content,
    Title: StyledTitle,
    Description: StyledDescription,
    Close: DialogClose,
    Actions: DialogActions,
    SelectTrigger: DialogSelectTrigger,
    SelectList: DialogSelectList,
    SelectItem: DialogSelectItem,
});
//# sourceMappingURL=Dialog.js.map